import React, { useEffect, useState, useCallback } from 'react';
import { fetchWithAuth } from '../services/api';

const StationSelector = ({ userId, onClose, initialStationName }) => {
  const [stations, setStations] = useState([]);
  const [checkedStations, setCheckedStations] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingStationId, setSavingStationId] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedStation, setSelectedStation] = useState("");
  const [verksamhetsOmraden, setVerksamhetsOmraden] = useState([]);

  const loadStations = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/stationer/${userId}`);
      if (!response.ok) throw new Error("Kunde inte hämta stationer");
      const data = await response.json();
      setStations(data);
      const initialChecked = {};
      data.forEach(s => (initialChecked[s.id] = s.active));
      setCheckedStations(initialChecked);
      return data;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!initialStationName) return;

    const init = async () => {
      const data = await loadStations();
      // Hemmastationen - fast och kan inte ändras
      const selected = data.find(s => s.name === initialStationName);
      if (selected) setSelectedStation(selected);
    };
    init();
  }, [userId, initialStationName, loadStations]);

  useEffect(() => {
    if (savingStationId === null) return;
    loadStations();
  }, [savingStationId, loadStations]);

  useEffect(() => {
    fetchWithAuth('/api/verksamhetsomraden')
      .then(res => (res.ok ? res.json() : []))
      .then(setVerksamhetsOmraden)
      .catch(() => setVerksamhetsOmraden([]));
  }, []);

  const handleClose = async () => {
    const selectedIds = stations.filter(s => s.active).map(s => s.id);
    if (selectedIds.length === 0) {
      alert("Minst en station måste vara vald.");
      return;
    }
    try {
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const handleBooleanChange = async (userId, stationId, field, value) => {
    setSavingStationId(stationId);
    try {
      const payload = { userId, stationId, field, value };
      const response = await fetchWithAuth(`/api/stationer/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setStations(prev => prev.map(s => s.id === stationId ? { ...s, [field]: value } : s));
        setCheckedStations(prev => ({
          ...prev,
          [stationId]: value
        }));
      } else {
        const errorText = await response.text();
        console.error(`Fel vid uppdatering av ${field}:`, errorText);
        alert(`Kunde inte uppdatera ${field}. Försök igen.`);
      }
    } catch (error) {
      console.error('Fel vid kommunikation med servern:', error);
      alert('Ett fel uppstod. Försök igen.');
    } finally { setSavingStationId(null); }
  };

  // Sätter aktiv-status för flera stationer i ett svep. Hemmastationen är alltid aktiv.
  const applyStationChanges = async (targetActiveByStationId) => {
    setBulkSaving(true);
    try {
      const changes = stations.filter(s => targetActiveByStationId.get(s.id) !== s.active);
      const results = await Promise.all(changes.map(s =>
        fetchWithAuth('/api/stationer/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, stationId: s.id, field: 'active', value: targetActiveByStationId.get(s.id) })
        })
      ));
      if (results.some(r => !r.ok)) {
        alert('Kunde inte uppdatera alla stationer. Försök igen.');
      }
      await loadStations();
    } catch (err) {
      console.error('Fel vid kommunikation med servern:', err);
      alert('Ett fel uppstod. Försök igen.');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleClearStations = () => {
    if (!selectedStation.id) return;
    if (!window.confirm('Ta bort alla stationer utom hemmastationen?')) return;

    const targets = new Map(stations.map(s => [s.id, s.id === selectedStation.id]));
    applyStationChanges(targets);
  };

  const handleSelectByVo = () => {
    if (!selectedStation.id) return;

    // En station kan ingå i flera verksamhetsområden - ta med samtliga.
    const matchandeVo = verksamhetsOmraden.filter(vo => vo.stationer.includes(selectedStation.name));
    if (matchandeVo.length === 0) {
      alert(`Hemmastationen "${selectedStation.name}" ingår inte i något verksamhetsområde.`);
      return;
    }
    if (!window.confirm('Ersätt vald stationslista med stationerna enligt verksamhetsområdet?')) return;

    const stationNamnEnligtVo = new Set(matchandeVo.flatMap(vo => vo.stationer));
    stationNamnEnligtVo.add(selectedStation.name);

    const targets = new Map(stations.map(s => [s.id, stationNamnEnligtVo.has(s.name)]));
    applyStationChanges(targets);
  };

  if (loading) return <p>Laddar stationer...</p>;
  if (error) return <p className="tw-text-red-500">{error}</p>;

  const hemmaVoNamn = verksamhetsOmraden
    .filter(vo => vo.stationer.includes(selectedStation.name))
    .map(vo => vo.name);

  return (
    <div className="tw-w-full tw-h-full tw-bg-gray-50 tw-border tw-rounded-xl tw-p-4">
      {/* Rubrik + hemmastation på samma rad */}
      <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
        <h3 className="tw-font-semibold tw-text-lg tw-mb-0">Hemmastation: </h3>
        <span
          title="Hemmastationen är fast och kan inte ändras här"
          className="tw-w-[150px] tw-border tw-bg-gray-100 tw-p-2 tw-text-sm tw-leading-normal tw-font-bold tw-rounded"
        >
          {selectedStation.name || '–'}
        </span>
        <h3 className="tw-font-semibold tw-text-lg tw-mb-0">VO:</h3>
        <span className="tw-text-sm tw-text-gray-700">
          {hemmaVoNamn.length > 0 ? hemmaVoNamn.join(', ') : '–'}
        </span>
        <h3 className="tw-font-semibold tw-text-lg tw-mb-0 tw-ml-10">Stationer:</h3>
      </div>

      {/* Checkbox-grid för stationer */}
      <div className="tw-grid tw-grid-cols-4 tw-gap-3 tw-overflow-y-auto tw-max-h-[400px]">
        {stations.map((s) => (
          <label
            key={s.id}
            className="tw-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-px-2 tw-py-1 hover:tw-bg-blue-50"
          >
            <input
              type="checkbox"
              checked={!!checkedStations[s.id]}
              onChange={e => handleBooleanChange(userId, s.id, "active", e.target.checked)}
              disabled={savingStationId === s.id || bulkSaving || s.id === selectedStation.id}
              className="tw-w-4 tw-h-4 tw-cursor-pointer"
            />
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">{s.name}</span>
          </label>
        ))}
      </div>
      <div className="tw-flex tw-justify-between tw-mt-4">
        <div className="tw-flex tw-gap-2">
          <button
            onClick={handleClearStations}
            disabled={bulkSaving}
            className="tw-bg-gray-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-gray-600 disabled:tw-opacity-50"
          >
            Rensa stationer
          </button>
          <button
            onClick={handleSelectByVo}
            disabled={bulkSaving}
            className="tw-bg-indigo-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-indigo-700 disabled:tw-opacity-50"
          >
            Välj stationer enligt VO
          </button>
        </div>
        <button
          onClick={handleClose}
          disabled={bulkSaving}
          className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-blue-700 disabled:tw-opacity-50"
        >
          Lämna stationer
        </button>
      </div>
    </div>
  );
};

export default StationSelector;
